import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "city", label: "Ville", type: "text" },
  { key: "postal_code", label: "Code postal", type: "text" },
  { key: "phone", label: "Téléphone", type: "tel" },
  { key: "client_email", label: "Email", type: "email", warn: true },
  { key: "notes", label: "Notes", type: "textarea" },
];

/**
 * Dialog de modification en masse des clients sélectionnés.
 * Props: { open, onClose, selectedKeys, onUpdated }
 */
export const BulkEditClientsDialog = ({ open, onClose, selectedKeys, onUpdated }) => {
  const [enabledFields, setEnabledFields] = useState({});
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setEnabledFields({});
    setValues({});
  };
  const close = () => {
    reset();
    onClose();
  };

  const toggleField = (key) => {
    setEnabledFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const setValue = (key, v) => {
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  const handleApply = async () => {
    const fields = {};
    for (const f of FIELDS) {
      if (!enabledFields[f.key]) continue;
      const val = values[f.key];
      if (val === undefined) continue;
      fields[f.key] = val;
    }
    if (Object.keys(fields).length === 0) {
      toast.error("Cochez au moins un champ et renseignez sa valeur");
      return;
    }
    setSaving(true);
    try {
      const res = await api.bulkUpdateClients({ client_keys: selectedKeys, fields });
      const d = res.data;
      toast.success(
        `${d.profiles_updated} profil(s) et ${d.orders_updated} commande(s) mis à jour`
      );
      if (onUpdated) onUpdated();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-xl" data-testid="bulk-edit-clients-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" />
            Modifier {selectedKeys.length} client{selectedKeys.length > 1 ? "s" : ""}
          </DialogTitle>
          <DialogDescription>
            Cochez les champs à modifier. Les valeurs seront propagées aux profils ET aux commandes rattachées.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {FIELDS.map((f) => (
            <div key={f.key} className="grid grid-cols-12 gap-3 items-start">
              <div className="col-span-5 flex items-center gap-2 pt-2">
                <Checkbox
                  id={`bulk-c-${f.key}`}
                  checked={!!enabledFields[f.key]}
                  onCheckedChange={() => toggleField(f.key)}
                  data-testid={`bulk-edit-clients-toggle-${f.key}`}
                />
                <Label htmlFor={`bulk-c-${f.key}`} className="cursor-pointer flex items-center gap-1">
                  {f.label}
                  {f.warn && <AlertTriangle className="h-3 w-3 text-amber-600" title="Modifier l'email change la clé d'agrégation" />}
                </Label>
              </div>
              <div className={`col-span-7 ${enabledFields[f.key] ? "" : "opacity-40 pointer-events-none"}`}>
                {f.type === "textarea" ? (
                  <Textarea
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValue(f.key, e.target.value)}
                    placeholder={f.label}
                    rows={2}
                    data-testid={`bulk-edit-clients-${f.key}`}
                  />
                ) : (
                  <Input
                    type={f.type}
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValue(f.key, e.target.value)}
                    placeholder={f.label}
                    data-testid={`bulk-edit-clients-${f.key}`}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={saving}>Annuler</Button>
          <Button onClick={handleApply} disabled={saving} data-testid="bulk-edit-clients-apply">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkEditClientsDialog;
